import React, { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import GlassCombobox from "./GlassCombobox";
import "./AutoSourcing.css";

export const INACTIVE_CATEGORY_MESSAGE =
  "This category is no longer available. Choose another category.";

// Single-select category state:
//   null                                   → nothing chosen ("Select category")
//   { mode: "all" }                        → All
//   { mode: "category", category_id, name } → one real backend category
//   { mode: "custom", user_query, resolver: "raw_query" } → Custom search
export function selectionLabel(selection) {
  if (!selection) return null;
  if (selection.mode === "all") return "All";
  if (selection.mode === "custom") return "Custom";
  if (selection.mode === "category") return selection.name || "Category";
  return null;
}

export function isSelectionReady(selection, { categoryAvailable = true } = {}) {
  if (!selection) return false;
  if (selection.mode === "all") return true;
  if (selection.mode === "category") return Boolean(selection.category_id) && categoryAvailable;
  if (selection.mode === "custom") return Boolean((selection.user_query || "").trim());
  return false;
}

export const CATEGORY_LIST_MAX_HEIGHT = 380;
const SEARCH_RESULT_LIMIT = 200;

// Container path segments are never meaningful parent labels.
const STRUCTURAL_PARENTS = new Set([
  "collections", "collection", "categories", "category", "departments", "department",
  "products", "product", "pages", "shop", "c", "all",
]);

export function categoryParentLabel(c) {
  const parent = (c?.parent_name || "").trim();
  if (!parent || STRUCTURAL_PARENTS.has(parent.toLowerCase())) return null;
  if (parent.toLowerCase() === (c?.name || "").trim().toLowerCase()) return null;
  return parent;
}

function breadcrumbOf(c) {
  if (Array.isArray(c.breadcrumb) && c.breadcrumb.length) return c.breadcrumb;
  if (Array.isArray(c.metadata?.breadcrumb) && c.metadata.breadcrumb.length) return c.metadata.breadcrumb;
  return null;
}

/** Parent path shown under a search result ("Tools > Power Tools"). */
export function categoryPathLabel(c) {
  const crumbs = breadcrumbOf(c);
  if (crumbs && crumbs.length > 1 && crumbs[crumbs.length - 1] === c.name) {
    return crumbs.slice(0, -1).join(" > ");
  }
  return categoryParentLabel(c);
}

/** Parent/children index over the flat category list. Unknown parents make a root. */
export function buildCategoryTree(categories) {
  const byId = new Map();
  categories.forEach((c) => {
    if (c?.category_id && !byId.has(c.category_id)) byId.set(c.category_id, c);
  });
  const children = new Map();
  const roots = [];
  byId.forEach((c) => {
    const pid = c.parent_category_id;
    if (pid && pid !== c.category_id && byId.has(pid)) {
      if (!children.has(pid)) children.set(pid, []);
      children.get(pid).push(c);
    } else {
      roots.push(c);
    }
  });
  // The retailer's brand root (backend taxonomy flags) comes before store
  // categories; each level otherwise keeps the retailer's navigation order.
  const isBrandRoot = (c) => c.node_type === "brand_root" || c.taxonomy_type === "brand";
  roots.splice(0, roots.length, ...roots.filter(isBrandRoot), ...roots.filter((c) => !isBrandRoot(c)));
  const parentOf = (id) => {
    const pid = byId.get(id)?.parent_category_id;
    return pid && byId.has(pid) && pid !== id ? pid : null;
  };
  const pathTo = (id) => {
    const out = [];
    const seen = new Set();
    let cur = parentOf(id);
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      out.unshift(cur);
      cur = parentOf(cur);
    }
    return out;
  };
  return { byId, roots, children, pathTo };
}

const isSelectable = (c) => c.selectable !== false && Boolean(c.url);

/** Rank for a search query across all levels; null when it does not match.
 *  exact name < name prefix < word prefix < name substring < path/alias. */
export function categorySearchRank(c, q) {
  const name = (c.normalized_name || c.name || "").toLowerCase();
  if (!q) return null;
  if (name === q) return 0;
  if (name.startsWith(q)) return 1;
  if (name.split(/[^a-z0-9]+/).some((w) => w && w.startsWith(q))) return 2;
  if (name.includes(q)) return 3;
  const extra = [
    ...(breadcrumbOf(c) || []),
    categoryParentLabel(c) || "",
    ...(Array.isArray(c.aliases) ? c.aliases : []),
    ...(Array.isArray(c.metadata?.aliases) ? c.metadata.aliases : []),
  ].join(" ").toLowerCase();
  return extra.includes(q) ? 4 : null;
}

export default function CategoryPicker({
  categories = [],
  value = null,
  onChange,
  onCustomQueryChange,
  disabled = false,
  loading = false,
  placeholder = "Select category",
  inactiveSelection = false,
  id,
}) {
  const [query, setQuery] = useState("");
  const [path, setPath] = useState([]);
  const tree = useMemo(() => buildCategoryTree(categories), [categories]);

  const q = query.trim().toLowerCase();
  const current = path.length ? tree.byId.get(path[path.length - 1]) : null;

  const results = useMemo(() => {
    if (!q) return [];
    const ranked = [];
    tree.byId.forEach((c) => {
      const rank = categorySearchRank(c, q);
      if (rank !== null) ranked.push({ c, rank, depth: (breadcrumbOf(c) || [c.name]).length });
    });
    ranked.sort((a, b) => a.rank - b.rank || a.depth - b.depth || a.c.name.localeCompare(b.c.name));
    return ranked.slice(0, SEARCH_RESULT_LIMIT).map((r) => r.c);
  }, [tree, q]);

  const isSelected = (selection) => {
    if (!value || selection.mode !== value.mode) return false;
    if (value.mode === "category") return selection.category_id === value.category_id;
    return true;
  };

  const choose = (selection) => {
    if (selection.mode === "custom") {
      const keep = value?.mode === "custom" ? value.user_query || "" : "";
      onChange({ mode: "custom", user_query: keep, resolver: "raw_query" });
    } else {
      onChange(selection);
    }
  };

  const drillTo = (nextPath) => {
    setPath(nextPath);
    setQuery("");
  };

  const onOpenChange = (open) => {
    if (!open) return;
    const selectedId = value?.mode === "category" ? value.category_id : null;
    setPath(selectedId && tree.byId.has(selectedId) ? tree.pathTo(selectedId) : []);
  };

  const categoryOption = (c, { subtitle = null } = {}) => {
    const kids = tree.children.get(c.category_id) || [];
    const selection = { mode: "category", category_id: c.category_id, name: c.name, url: c.url };
    if (kids.length && !q) {
      return {
        key: `cat:${c.category_id}`,
        label: c.name,
        description: subtitle,
        expandable: true,
        keepOpen: true,
        selected: false,
        title: `Open ${c.name}`,
        onSelect: () => drillTo([...tree.pathTo(c.category_id), c.category_id]),
      };
    }
    if (!isSelectable(c)) {
      return {
        key: `cat:${c.category_id}`,
        label: c.name,
        description: subtitle,
        expandable: kids.length > 0,
        keepOpen: true,
        selected: false,
        onSelect: () => drillTo([...tree.pathTo(c.category_id), c.category_id]),
      };
    }
    return {
      key: `cat:${c.category_id}`,
      label: c.name,
      description: subtitle,
      selected: isSelected(selection),
      onSelect: () => choose(selection),
    };
  };

  const allOption = { key: "all", label: "All", selected: isSelected({ mode: "all" }), onSelect: () => choose({ mode: "all" }) };
  const customOption = (before) => ({
    key: "custom",
    label: "Custom search...",
    separated: true,
    before,
    selected: isSelected({ mode: "custom" }),
    onSelect: () => choose({ mode: "custom" }),
  });

  let options;
  if (q) {
    options = [
      allOption,
      ...results.map((c) => categoryOption(c, { subtitle: categoryPathLabel(c) })),
      customOption(results.length === 0 ? <div className="as-glass-empty">No matching categories.</div> : null),
    ];
  } else if (current) {
    const kids = tree.children.get(current.category_id) || [];
    const parentSelection = { mode: "category", category_id: current.category_id, name: current.name, url: current.url };
    options = [
      ...(isSelectable(current)
        ? [{
            key: `all-in:${current.category_id}`,
            label: `All ${current.name}`,
            className: "as-category-select-parent",
            selected: isSelected(parentSelection),
            onSelect: () => choose(parentSelection),
          }]
        : []),
      ...kids.map((c) => categoryOption(c)),
    ];
  } else {
    options = [
      allOption,
      ...tree.roots.map((c) => categoryOption(c)),
      customOption(loading ? <div className="as-glass-empty">Loading categories…</div> : null),
    ];
  }

  const selectedNode = value?.mode === "category" ? tree.byId.get(value.category_id) : null;
  const selectedPath = selectedNode && tree.pathTo(selectedNode.category_id).length
    ? tree.pathTo(selectedNode.category_id).map((cid) => tree.byId.get(cid)?.name).join(" > ")
    : null;

  const header = current && !q ? (
    <nav className="as-category-crumbs" aria-label="Category path" data-testid="category-picker-crumbs">
      <button
        type="button"
        className="as-category-back"
        aria-label="Back"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => drillTo(path.slice(0, -1))}
        data-testid="category-picker-back"
      >
        <ArrowLeft size={14} aria-hidden="true" />
      </button>
      <span className="as-category-crumb-list">
        <button
          type="button"
          className="as-category-crumb"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => drillTo([])}
        >
          All Categories
        </button>
        {path.map((cid, i) => {
          const last = i === path.length - 1;
          // Long paths keep the first and the last two levels visible.
          const collapsed = path.length > 3 && i > 0 && i < path.length - 2;
          if (collapsed) {
            return i === 1 ? (
              <React.Fragment key={cid}>
                <span className="as-category-crumb-sep" aria-hidden="true">›</span>
                <span
                  className="as-category-crumb-more"
                  title={path.map((p) => tree.byId.get(p)?.name).join(" › ")}
                  data-testid="category-picker-crumbs-more"
                >
                  …
                </span>
              </React.Fragment>
            ) : null;
          }
          return (
            <React.Fragment key={cid}>
              <span className="as-category-crumb-sep" aria-hidden="true">›</span>
              <button
                type="button"
                className="as-category-crumb"
                aria-current={last ? "page" : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => !last && drillTo(path.slice(0, i + 1))}
              >
                {tree.byId.get(cid)?.name}
              </button>
            </React.Fragment>
          );
        })}
      </span>
    </nav>
  ) : null;

  return (
    <div className="as-category-picker">
      <GlassCombobox
        id={id}
        testId="category-picker"
        label={selectionLabel(value)}
        placeholder={placeholder}
        options={options.map(({ className, ...o }) => (className ? { ...o, label: <span className={className}>{o.label}</span> } : o))}
        disabled={disabled}
        query={query}
        onQueryChange={setQuery}
        searchPlaceholder="Search all categories..."
        searchLabel="Search categories"
        listLabel={current ? `${current.name} categories` : "Categories"}
        maxHeight={CATEGORY_LIST_MAX_HEIGHT}
        header={header}
        onBack={path.length ? () => drillTo(path.slice(0, -1)) : undefined}
        resetKey={`${path.join("/")}|${q ? "search" : "browse"}`}
        onOpenChange={onOpenChange}
      />

      {selectedPath && (
        <div className="as-category-path" data-testid="category-picker-path">{selectedPath}</div>
      )}

      {value?.mode === "custom" && (
        <input
          className="as-input as-category-custom"
          value={value.user_query || ""}
          onChange={(e) => onCustomQueryChange?.(e.target.value)}
          placeholder="What are you looking for?"
          aria-label="What are you looking for?"
          data-testid="auto-sourcing-custom-query"
          disabled={disabled}
        />
      )}

      {inactiveSelection && value?.mode === "category" && (
        <div className="as-inline-error" role="alert" data-testid="category-picker-inactive">
          {INACTIVE_CATEGORY_MESSAGE}
        </div>
      )}
    </div>
  );
}
